import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import api from '../utils/api';

/**
 * Landing page for both `/reset-password?token=...` (forgot-password) and
 * the welcome-set-password link sent after a contact-form / register-quick
 * signup. In both cases the backend endpoint is POST /auth/reset-password
 * with { token, password }.
 */
const ResetPasswordPage: React.FC = () => {
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const navigate = useNavigate();

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!token) {
      setError('No reset token in the link.');
      return;
    }
    if (password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }
    setBusy(true);
    try {
      await api.post('/auth/reset-password', { token, password });
      setDone(true);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Could not set password. The link may have expired.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="bg-cream min-h-[60vh]">
      <Helmet>
        <title>Set your password — EJL Education</title>
      </Helmet>
      <section className="section">
        <div className="container-content max-w-[48ch]">
          <h1 className="mb-4">{done ? 'Password set' : 'Set your password'}</h1>
          {done ? (
            <>
              <p className="text-body text-ink/90 mb-6">
                You can now log in with your email and this password.
              </p>
              <button
                onClick={() => navigate('/login')}
                className="btn btn-primary"
              >
                Log in
              </button>
            </>
          ) : (
            <form onSubmit={submit} noValidate className="space-y-6">
              <p className="text-body text-ink/90">
                Choose a password for your account. Minimum 6 characters.
              </p>
              {error && (
                <div className="border border-rule bg-paper p-4">
                  <p className="text-ink text-[15px]">{error}</p>
                </div>
              )}
              <div>
                <label htmlFor="password" className="block text-[15px] font-medium text-ink mb-2">
                  New password
                </label>
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="input"
                  autoComplete="new-password"
                  required
                  minLength={6}
                />
              </div>
              <div>
                <label htmlFor="confirm" className="block text-[15px] font-medium text-ink mb-2">
                  Confirm password
                </label>
                <input
                  id="confirm"
                  type="password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  className="input"
                  autoComplete="new-password"
                  required
                  minLength={6}
                />
              </div>
              <div className="flex justify-between items-center pt-2">
                <Link to="/login" className="text-sm text-muted hover:text-ochre">
                  Back to login
                </Link>
                <button
                  type="submit"
                  disabled={busy}
                  className="btn btn-primary disabled:opacity-50"
                >
                  {busy ? 'Saving…' : 'Set password'}
                </button>
              </div>
            </form>
          )}
        </div>
      </section>
    </main>
  );
};

export default ResetPasswordPage;
